import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, TicketPlus, Check, UploadCloud } from "lucide-react";
import { IDIOMAS, labelModalidadPortal } from "@/lib/constants";
import ImportarReservasDialog from "@/components/reservas/ImportarReservasDialog";

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const money = (v, m) => (v == null ? "—" : `${m === "USD" ? "$" : ""}${Number(v).toFixed(2)}${m && m !== "USD" ? " " + m : ""}`);
const slotKey = (s) => `${s.tour_id}|${s.fecha}|${s.hora}`;
const opcionLabel = (o) => labelModalidadPortal(o.modalidad, o.vehiculo) + (o.es_privado ? "" : ` · ${IDIOMAS[o.idioma] || o.idioma}`);

export default function PortalNuevaReserva() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.slot;
  const presetOpcion = location.state?.presetOpcion;

  const [tours, setTours] = useState([]);
  const [tourId, setTourId] = useState(prefill?.tour_id || "");
  const [fecha, setFecha] = useState(prefill?.fecha || hoyISO());
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(prefill || null);
  const [opcion, setOpcion] = useState(presetOpcion || (prefill && prefill.opciones?.length === 1 ? prefill.opciones[0] : null));
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [adultos, setAdultos] = useState(1);
  const [ninos, setNinos] = useState(0);
  const [infantes, setInfantes] = useState(0);
  const [variante, setVariante] = useState("sin_barco");
  const [pasajero, setPasajero] = useState({ nombre: "", apellido: "", email: "", telefono: "" });
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    api.get("/portal/tours").then(({ data }) => setTours(data)).catch(() => {});
  }, []);

  const cargarSlots = useCallback(async () => {
    if (!tourId || !fecha) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    try {
      const { data } = await api.get("/portal/disponibilidad", { params: { desde: fecha, hasta: fecha, tour_id: tourId } });
      const list = (data.slots || []).filter((s) => s.disponible);
      setSlots(list);
      setSelected((prev) => {
        if (!prev) return null;
        const fresh = list.find((s) => slotKey(s) === slotKey(prev)) || null;
        setOpcion((prevOp) => {
          if (!fresh) return null;
          if (fresh.opciones.length === 1) return fresh.opciones[0];
          return fresh.opciones.find((o) => prevOp && o.modalidad === prevOp.modalidad && o.idioma === prevOp.idioma) || null;
        });
        return fresh;
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingSlots(false);
    }
  }, [tourId, fecha]);

  useEffect(() => {
    cargarSlots();
  }, [cargarSlots]);

  useEffect(() => {
    setVariante("sin_barco");
  }, [opcion?.tour_id, opcion?.modalidad, opcion?.idioma]);

  const elegirSlot = (s) => {
    setSelected(s);
    setOpcion(s.opciones.length === 1 ? s.opciones[0] : null);
  };

  const pax = Number(adultos || 0) + Number(ninos || 0) + Number(infantes || 0);
  const precios = opcion?.requiere_variante ? opcion.precios_variante?.[variante] : opcion;
  const total = opcion && precios?.tiene_tarifa
    ? Number(precios.precio_adulto || 0) * Number(adultos || 0) +
      Number(precios.precio_nino || 0) * Number(ninos || 0) +
      Number(precios.precio_infante || 0) * Number(infantes || 0)
    : null;
  const excedeCupo = opcion && !opcion.es_privado && pax > opcion.libre;

  const guardar = async () => {
    if (!selected) return toast.error("Elegí un horario disponible.");
    if (!opcion) return toast.error("Elegí una opción (modalidad/idioma) dentro del horario.");
    if (!pasajero.nombre.trim()) return toast.error("Indicá el nombre del cliente.");
    if (!pasajero.telefono.trim()) return toast.error("Indicá el teléfono del cliente (obligatorio).");
    if (Number(adultos || 0) < 1) return toast.error("Debe haber al menos 1 adulto.");
    if (excedeCupo) return toast.error(`No hay cupo suficiente (libres: ${opcion.libre}).`);
    setSaving(true);
    try {
      const payload = {
        tour_id: opcion.tour_id,
        fecha: selected.fecha,
        hora: selected.hora,
        modalidad: opcion.modalidad,
        vehiculo: opcion.vehiculo,
        idioma: opcion.idioma,
        adultos: Number(adultos || 0),
        ninos: Number(ninos || 0),
        infantes: Number(infantes || 0),
        pasajero: { ...pasajero, idioma: opcion.idioma },
        notas,
      };
      if (opcion.requiere_variante) payload.variante = variante;
      const { data } = await api.post("/portal/reservas", payload);
      if (opcion.es_privado) {
        toast.success(`Solicitud de privado enviada (${data.reserva.codigo_booking}). Tours NY la aceptará o rechazará según disponibilidad de vehículo.`);
      } else {
        toast.success(`Reserva confirmada (${data.reserva.codigo_booking}). Cupo descontado.`);
      }
      navigate("/portal/reservas");
    } catch (e) {
      const code = e.response?.status;
      toast.error(code === 409 ? formatApiError(e.response?.data?.detail) : formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="portal-nueva-reserva">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <TicketPlus className="h-6 w-6 text-indigo-600" /> Nueva reserva
          </h2>
          <p className="text-sm text-slate-500 mt-1">Se confirma al instante y descuenta el cupo. Si está al 100%, no se podrá cargar.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setImportOpen(true)}
          data-testid="portal-carga-masiva-button"
          className="gap-2 shrink-0"
        >
          <UploadCloud className="h-4 w-4" /> Carga masiva (archivo)
        </Button>
      </div>

      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Tour</Label>
            <Select value={tourId} onValueChange={(v) => { setTourId(v); setSelected(null); setOpcion(null); }}>
              <SelectTrigger data-testid="nueva-tour-select"><SelectValue placeholder="Elegí un tour" /></SelectTrigger>
              <SelectContent>
                {tours.map((t) => (<SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => { setFecha(e.target.value); setSelected(null); setOpcion(null); }} data-testid="nueva-fecha" />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Horario disponible</Label>
          {loadingSlots ? (
            <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" /></div>
          ) : !tourId ? (
            <p className="text-sm text-slate-400 py-4">Elegí un tour y una fecha para ver horarios.</p>
          ) : slots.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="nueva-sin-slots">No hay horarios disponibles para esa fecha.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {slots.map((s, i) => {
                const active = selected && slotKey(selected) === slotKey(s);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => elegirSlot(s)}
                    data-testid="nueva-slot-option"
                    className={`text-left rounded-md border px-3 py-2.5 transition-colors ${
                      active ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-semibold text-slate-900">{s.hora}</span>
                      {active && <Check className="h-4 w-4 text-indigo-600" />}
                    </div>
                    {s.n_opciones > 1 ? (
                      <div className="text-xs text-indigo-600 font-medium mt-0.5">
                        {s.n_opciones} opciones disponibles
                      </div>
                    ) : (
                      <div className="text-xs text-slate-600 mt-0.5">
                        {opcionLabel(s.opciones[0])}
                      </div>
                    )}
                    {s.es_privado ? (
                      <div className="text-xs text-violet-600 font-medium mt-0.5">Por solicitud (a confirmar con Tours NY)</div>
                    ) : (
                      <div className="text-xs text-emerald-600 font-medium mt-0.5" data-testid="nueva-slot-libres">
                        {/* PROMPT 122: solo los libres, nunca la capacidad de la salida */}
                        {s.libre} {s.libre === 1 ? "cupo libre" : "cupos libres"}{s.n_opciones > 1 ? " (principal, ver opciones)" : ""}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {selected && selected.opciones.length > 1 && (
          <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="nueva-opciones-section">
            <Label className="text-xs">Elegí la opción (modalidad / idioma) dentro de este horario</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {selected.opciones.map((o, i) => {
                const active = opcion && opcion.modalidad === o.modalidad && opcion.idioma === o.idioma && opcion.tour_id === o.tour_id;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={!o.disponible}
                    onClick={() => setOpcion(o)}
                    data-testid="nueva-opcion-item"
                    className={`text-left rounded-md border px-3 py-2 transition-colors ${
                      !o.disponible ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed" :
                      active ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs font-medium text-slate-800">
                      {opcionLabel(o)}
                      {active && <Check className="h-3.5 w-3.5 text-indigo-600" />}
                    </div>
                    <div className={`text-xs font-medium mt-0.5 ${o.disponible ? "text-emerald-600" : "text-red-500"}`}>
                      {o.disponible ? `${o.libre} cupos libres` : "Sin cupo (esta modalidad)"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {opcion?.requiere_variante && (
          <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="nueva-variante-section">
            <Label className="text-xs">Cataratas del Niágara: ¿con crucero en barco?</Label>
            <div className="grid grid-cols-2 gap-2">
              {["sin_barco", "con_barco"].map((v) => {
                const p = opcion.precios_variante?.[v];
                const active = variante === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVariante(v)}
                    data-testid={v === "con_barco" ? "nueva-variante-con-barco" : "nueva-variante-sin-barco"}
                    className={`text-left rounded-md border px-3 py-2 transition-colors ${
                      active ? "border-cyan-500 bg-cyan-50 ring-1 ring-cyan-500" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-800">
                      {v === "con_barco" ? "CON BARCO" : "SIN BARCO"}
                      {active && <Check className="h-3.5 w-3.5 text-cyan-600" />}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {p?.tiene_tarifa ? money(p.precio_adulto, p.moneda) + " / adulto" : "Sin tarifa cargada"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-4">
        <h3 className="font-heading font-semibold text-slate-900">Pasajeros y cliente</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Adultos</Label>
            <Input type="number" min={1} value={adultos} onChange={(e) => setAdultos(e.target.value)} data-testid="nueva-adultos" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Niños (4-17)</Label>
            <Input type="number" min={0} value={ninos} onChange={(e) => setNinos(e.target.value)} data-testid="nueva-ninos" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Infantes (0-3)</Label>
            <Input type="number" min={0} value={infantes} onChange={(e) => setInfantes(e.target.value)} data-testid="nueva-infantes" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Nombre del cliente</Label>
            <Input value={pasajero.nombre} onChange={(e) => setPasajero({ ...pasajero, nombre: e.target.value })} data-testid="nueva-cliente-nombre" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Apellido</Label>
            <Input value={pasajero.apellido} onChange={(e) => setPasajero({ ...pasajero, apellido: e.target.value })} data-testid="nueva-cliente-apellido" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Email (opcional)</Label>
            <Input type="email" value={pasajero.email} onChange={(e) => setPasajero({ ...pasajero, email: e.target.value })} data-testid="nueva-cliente-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Teléfono <span className="text-red-500">*</span></Label>
            <Input value={pasajero.telefono} onChange={(e) => setPasajero({ ...pasajero, telefono: e.target.value })} data-testid="nueva-cliente-telefono" required />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Notas (opcional)</Label>
          <Input value={notas} onChange={(e) => setNotas(e.target.value)} data-testid="nueva-notas" />
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2 border-t border-slate-100">
          <div className="text-sm">
            <span className="text-slate-500">Total estimado: </span>
            <span className="font-semibold text-slate-900" data-testid="nueva-total">
              {total == null ? "Sin tarifa cargada" : money(total, precios?.moneda || opcion?.moneda)}
            </span>
            {excedeCupo && <span className="text-red-600 ml-3 text-xs">Excede el cupo disponible ({opcion.libre}).</span>}
            {selected && selected.opciones.length > 1 && !opcion && (
              <span className="text-amber-600 ml-3 text-xs">Elegí una opción arriba para continuar.</span>
            )}
          </div>
          <Button onClick={guardar} disabled={saving || !selected || !opcion || excedeCupo} className="gap-2" data-testid="nueva-confirmar">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : opcion?.es_privado ? "Solicitar privado" : "Confirmar reserva"}
          </Button>
        </div>
      </Card>

      <ImportarReservasDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        basePath="/portal"
        onImported={() => navigate("/portal/reservas")}
      />
    </div>
  );
}
